import { useState, useCallback, useEffect } from "react";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
} from "@material-ui/core";

// core components
import StructureFields from "components/_refactor/fields/structure";
import HeaderClientes from "components/_refactor/headers/clients.js";
import ClearButton from "components/_refactor/button/clear";
import ResultsSummary from "components/Reports/ResultsSummary";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import EvolutionIndicators from "components/Reports/EvolutionIndicators";
import EvolutionStores from "components/Reports/EvolutionStores";
import IndicatorsHeatMap from "components/Reports/IndicatorsHeatMap";
import componentStyles from "assets/theme/views/admin/dashboard";
import componentStylesCardDeck from "assets/theme/components/cards/card-deck";
import UtilsFncs from "utils/UtilsFncs";
import Api from "lib/api/httpRequestHelper";
import LoadingData from "components/Loading/LoadingData";
import { IsMobile, IsMobile900 } from "utils/is-mobile";

const useStyles = makeStyles(componentStyles);
const useStylesCardDeck = makeStyles(componentStylesCardDeck);

function DashboardIndicadoresConversao() {
  const snackbar = useSnackbar();
  const classes = { ...useStyles(), ...useStylesCardDeck() };

  const [searchingResults, setSearchingResults] = useState(false);
  const [searchingIndicators, setSearchingIndicators] = useState(false);
  const [searchingStores, setSearchingStores] = useState(false);
  const [searchingHeatMap, setSearchingHeatMap] = useState(false);
  const [filters, setFilters] = useState({});
  const [resultsSummary, setResultsSummary] = useState(null);
  const [evolutionIndicators, setEvolutionIndicators] = useState(null);
  const [evolutionStores, setEvolutionStores] = useState(null);
  const [heatMapData, setHeatMapData] = useState(null);
  const [lastIndicatorsParams, setLastIndicatorsParams] = useState({});
  const [lastStoresParams, setLastStoresParams] = useState({});
  const [lastHeatMapParams, setLastHeatMapParams] = useState({});
  const [filtersLoading, setFiltersLoading] = useState([]);
  const [clearFilters, setClearFilters] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [hasNoBrand, setHasNoBrand] = useState(false);

  const isMobile900 = IsMobile900();
  const isMobile = IsMobile();

  const pesquisaEvolucaoIndicadores = useCallback(
    async ({ isAnnual, ...params }, shouldSetSearch) => {
      if (shouldSetSearch) {
        setSearchingIndicators(true);
      }

      if (evolutionIndicators) {
        setEvolutionIndicators(null);
      }

      setLastIndicatorsParams(
        isAnnual ? { ...params, dtIni: null, dtFim: null } : params
      );

      await Api.get(
        "sell-out-indicator-evolution",
        "selloutreport",
        UtilsFncs.RestParamsComposer({
          ...filters,
          idMarca: filters.idMarca ?? "TODAS",
          ...params,
        }),
        true
      )
        .then((data) => {
          setEvolutionIndicators(data?.Result?.DashboardVisitasList);
        })
        .catch((error) => {
          snackbar.showMessage(
            "Ocorreu um erro ao buscar Evolução Indicadores. Verifique os parâmetros informados.",
            {
              severity: "error",
            }
          );
          console.log(error);        });

      if (shouldSetSearch) {
        setSearchingIndicators(false);
      }
    },
    [evolutionIndicators, filters, snackbar]
  );

  const pesquisaEvolucaoLojas = useCallback(
    async ({ isAnnual, ...params }, shouldSetSearch) => {
      if (shouldSetSearch) {
        setSearchingStores(true);
      }

      if (evolutionStores) {
        setEvolutionStores(null);
      }

      setLastStoresParams(
        isAnnual ? { ...params, dtIni: null, dtFim: null } : params
      );

      await Api.get(
        "sell-out-indicator-store",
        "selloutreport",
        UtilsFncs.RestParamsComposer({
          ...filters,
          idMarca: filters.idMarca ?? "TODAS",
          ...params,
        }),
        true
      )
        .then((data) => {
          setEvolutionStores(data.Result);
        })
        .catch((error) => {
          snackbar.showMessage(
            "Ocorreu um erro ao buscar Evolução Lojas - Taxa de Conversão. Verifique os parâmetros informados.",
            {
              severity: "error",
            }
          );
          console.log(error);        });

      if (shouldSetSearch) {
        setSearchingStores(false);
      }
    },
    [evolutionStores, filters, snackbar]
  );

  const pesquisaMapaCalor = useCallback(
    async ({ isMonth, ...params }, shouldSetSearch) => {
      if (shouldSetSearch) {
        setSearchingHeatMap(true);
      }

      if (heatMapData) {
        setHeatMapData(null);
      }

      setLastHeatMapParams(
        isMonth ? { ...params, dtIni: null, dtFim: null } : params
      );

      await Api.get(
        "sell-out-indicator-heat-map",
        "selloutreport",
        UtilsFncs.RestParamsComposer({
          ...filters,
          idMarca: filters.idMarca ?? "TODAS",
          ...params,
        }),
        true
      )
        .then((data) => {
          setHeatMapData(data.Result);
        })
        .catch((error) => {
          snackbar.showMessage(
            "Ocorreu um erro ao buscar Mapa de Calor. Verifique os parâmetros informados.",
            {
              severity: "error",
            }
          );
          console.log(error);        });

      if (shouldSetSearch) {
        setSearchingHeatMap(false);
      }
    },
    [filters, heatMapData, snackbar]
  );

  const pesquisaFiltros = useCallback(async () => {
    setCancelLoading(false);
    setSearchingResults(true);
    setSearchingIndicators(true);
    setSearchingStores(true);
    setSearchingHeatMap(true);

    if (resultsSummary) {
      setResultsSummary(null);
    }

    if (evolutionIndicators) {
      setEvolutionIndicators(null);
    }

    if (evolutionStores) {
      setEvolutionStores(null);
    }

    if (heatMapData) {
      setHeatMapData(null);
    }

    await Api.get(
      "sell-out-indicator-summary",
      "selloutreport",
      UtilsFncs.RestParamsComposer({
        ...filters,
        idMarca: filters.idMarca ?? "TODAS",
      }) + "&dimensao=mensal",
      true
    )
      .then((data) => {
        setResultsSummary(data.Result);
      })
      .catch((error) => {
        snackbar.showMessage(
          "Ocorreu um erro ao buscar resumo de pedidos. Verifique os parâmetros informados.",
          {
            severity: "error",
          }
        );
        console.log(error);      });

    setSearchingResults(false);

    await pesquisaEvolucaoIndicadores(
      {
        dtIni: UtilsFncs.GetMoment().subtract(8, "days").format("YYYY-MM-DD"),
        dtFim: UtilsFncs.GetMoment().subtract(1, "days").format("YYYY-MM-DD"),
        dimensao: "diario",
      },
      false
    );

    setSearchingIndicators(false);

    await pesquisaEvolucaoLojas(
      {
        dtIni: UtilsFncs.GetMoment().subtract(8, "days").format("YYYY-MM-DD"),
        dtFim: UtilsFncs.GetMoment().subtract(1, "days").format("YYYY-MM-DD"),
        dimensao: "diario",
      },
      false
    );

    setSearchingStores(false);

    await pesquisaMapaCalor(
      {
        dtIni: UtilsFncs.GetMoment().subtract(8, "days").format("YYYY-MM-DD"),
        dtFim: UtilsFncs.GetMoment().subtract(1, "days").format("YYYY-MM-DD"),
        dimensao: "visitas",
        tipoPeriodo: "hora",
      },
      false
    );

    setSearchingHeatMap(false);
  }, [
    evolutionIndicators,
    evolutionStores,
    filters,
    heatMapData,
    pesquisaEvolucaoIndicadores,
    pesquisaEvolucaoLojas,
    pesquisaMapaCalor,
    resultsSummary,
    snackbar,
  ]);

  const handleClearFilters = () => {
    setSearchingResults(true);
    setSearchingIndicators(true);
    setSearchingStores(true);
    setSearchingHeatMap(true);
    setClearFilters(true);
    setSearchingResults(false);
    setSearchingIndicators(false);
    setSearchingStores(false);
    setSearchingHeatMap(false);
  };

  const handleStopSearching = () => {
    setCancelLoading(true);
    setSearchingResults(false);
    setSearchingIndicators(false);
    setSearchingStores(false);
    setSearchingHeatMap(false);
  };

  const handleVerifyRequiredInputs = () => {
    return pesquisaFiltros();
  };

  useEffect(() => {
    if (filters?.idMarca) {
      setHasNoBrand(false);
    }

    if (
      filters?.idMarca === null &&
      filters?.idCanal === "" &&
      filters?.idRepresentante === "" &&
      filters?.idGrupoEconomico === "" &&
      filters?.idLoja === 0
    ) {
      setClearFilters(false);
    }
  }, [filters]);

  return (
    <>
      <HeaderClientes
        section="Indicadores de Conversão"
        subsection="Dashboard"
      />
      <Container maxWidth={false} style={{ marginTop: "-4.5rem" }}>
        <Grid container>
          <Grid item xs={12}>
            <Card classes={{ root: classes.cardRoot }}>
              <CardHeader
                className={classes.cardHeader}
                title="Filtros"
                titleTypographyProps={{
                  component: Box,
                  marginBottom: "0!important",
                  variant: "h3",
                }}
              ></CardHeader>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <StructureFields
                    clearFilters={clearFilters}
                    onStructureFieldsChange={setFilters}
                    disableAll={
                      searchingResults || searchingIndicators || searchingStores
                    }
                    onStructureFieldsLoad={setFiltersLoading}
                    fieldsOrder={["economicGroup", "store"]}
                    hasNoBrand={hasNoBrand}
                  />
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={isMobile ? 12 : isMobile900 && 6}>
                    <Button
                      color="default"
                      variant="contained"
                      style={
                        isMobile || isMobile900
                          ? { width: "100%", marginBottom: "0.75rem" }
                          : { marginBottom: "0rem" }
                      }
                      onClick={handleVerifyRequiredInputs}
                      disabled={
                        searchingResults ||
                        searchingIndicators ||
                        searchingStores ||
                        filtersLoading.length > 0
                      }
                    >
                      {searchingResults ||
                      searchingIndicators ||
                      searchingStores
                        ? "Pesquisando..."
                        : "Pesquisar"}
                    </Button>
                  </Grid>
                  <Grid
                    item
                    xs={isMobile ? 12 : isMobile900 && 6}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <ClearButton
                      disable={
                        searchingResults ||
                        searchingIndicators ||
                        searchingStores ||
                        filtersLoading.length > 0
                      }
                      onClick={() => handleClearFilters()}
                      fullWidth={true}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            {searchingResults ? (
              <LoadingData
                searching={searchingResults}
                onClick={() => handleStopSearching()}
                classes={classes.root}
                label=" Buscando Resumo dos Resultados..."
              />
            ) : resultsSummary && !cancelLoading ? (
              <ResultsSummary
                results={resultsSummary}
                isMobile={isMobile}
                isMobile900={isMobile900}
              />
            ) : null}
          </Grid>
          <Grid item xs={12}>
            {searchingIndicators ? (
              <LoadingData
                searching={searchingIndicators}
                onClick={() => handleStopSearching()}
                classes={classes.root}
                label="Buscando Evolução Indicadores..."
              />
            ) : evolutionIndicators && !cancelLoading ? (
              <EvolutionIndicators
                isMobile={isMobile}
                indicators={evolutionIndicators}
                pesquisar={pesquisaEvolucaoIndicadores}
                lastIndicatorsParams={lastIndicatorsParams}
              />
            ) : null}
          </Grid>
          <Grid item xs={12}>
            {searchingStores ? (
              <LoadingData
                searching={searchingStores}
                onClick={() => handleStopSearching()}
                classes={classes.root}
                label="Buscando Evolução Lojas - Taxa de Conversão..."
              />
            ) : evolutionStores && !cancelLoading ? (
              <EvolutionStores
                isMobile={isMobile}
                stores={evolutionStores}
                pesquisar={pesquisaEvolucaoLojas}
                lastStoresParams={lastStoresParams}
              />
            ) : null}
          </Grid>
          <Grid item xs={12}>
            {searchingHeatMap ? (
              <LoadingData
                searching={searchingHeatMap}
                onClick={() => handleStopSearching()}
                classes={classes.root}
                label="Buscando Mapa de Calor..."
              />
            ) : heatMapData && !cancelLoading ? (
              <IndicatorsHeatMap
                isMobile={isMobile}
                dados={heatMapData}
                pesquisar={pesquisaMapaCalor}
                lastHeatMapParams={lastHeatMapParams}
              />
            ) : null}
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

export default DashboardIndicadoresConversao;
